import { InstanceHelper } from '~/helpers/instanceHelper';
import { IProcessFlowStepConstructorOptions } from '~/interfaces/processFlowInterfaces';
import ProcessFlow from '~/models/processFlows/processFlow';
import ProcessFlowInitialStep from '~/models/processFlows/processFlowInitialStep';
import ProcessFlowStep from '~/models/processFlows/processFlowStep';
import ProcessFlowStepJumpToFactory from '~/models/processFlows/processFlowStepJumpToFactory';
import { ProcessFlowExportCSVStep } from "~/models/processFlows/steps/exportCSVStep";
import { GenerateDocumentStep } from "~/models/processFlows/steps/generateDocumentStep";
import { ProcessFlowGenerateDocumentV2Step } from "~/models/processFlows/steps/processFlowGenerateDocumentV2Step";
import IterationStep from "~/models/processFlows/steps/iterationStep";
import { ProcessFlowFindDataStep } from "~/models/processFlows/steps/processFlowFindDataStep";
import { ProcessFlowSaveDataStep } from "~/models/processFlows/steps/processFlowSaveDataStep";
import { ProcessFlowSetDataStep } from "~/models/processFlows/steps/processFlowSetDataStep";
import { ProcessFlowRunProcessStep } from "~/models/processFlows/steps/processFlowRunProcessStep";
import { ProcessFlowStepType } from '../../enums/processFlowStepType';
import assertNever from "../../../global/assetNever";
import {ProcessFlowDeleteDataStep} from "~/models/processFlows/steps/processFlowDeleteDataStep";
import { ProcessFlowFindCountStep } from "~/models/processFlows/steps/processFlowFindCountStep";
import { ProcessFlowCallApiStep } from "~/models/processFlows/steps/processFlowCallApiStep";
import {ProcessFlowTakeActionStep} from '~/models/processFlows/steps/processFlowTakeActionStep'
import { ProcessFlowSendNotificationStep } from '~/models/processFlows/steps/processFlowSendNotificationStep'
import {ProcessFlowDisplayAFormStep} from '~/models/processFlows/steps/processFlowDisplayAFormStep'
import {ProcessFlowRedirectStep} from '~/models/processFlows/steps/processFlowRedirectStep'

export default class ProcessFlowStepFactory {
  static create(parent: ProcessFlow | IterationStep, options: IProcessFlowStepConstructorOptions = {stepType: null, json: {}}): ProcessFlowStep {
    options.json = options.json || {}
    let step: ProcessFlowStep = null
    switch (options.stepType) {
      case ProcessFlowStepType.RunIteration:
        step = new IterationStep(parent, options)
        break
      case ProcessFlowStepType.CallApi:
        step = new ProcessFlowCallApiStep(parent, options)
        break
      case ProcessFlowStepType.GenerateDocument:
        step = new GenerateDocumentStep(parent, options)
        break
      case ProcessFlowStepType.GenerateDocumentV2:
        step = new ProcessFlowGenerateDocumentV2Step(parent, options)
        break
      case ProcessFlowStepType.SendNotification:
        step = new ProcessFlowSendNotificationStep(parent, options)
        break
      case ProcessFlowStepType.RunProcess:
        step = new ProcessFlowRunProcessStep(parent, options)
        break
      case ProcessFlowStepType.InitialStep:
        step = new ProcessFlowInitialStep(parent, options)
        break
      case ProcessFlowStepType.SetData:
        step = new ProcessFlowSetDataStep(parent, options)
        break
      case ProcessFlowStepType.SaveData:
        step = new ProcessFlowSaveDataStep(parent, options)
        break
      case ProcessFlowStepType.DeleteData:
        step = new ProcessFlowDeleteDataStep(parent, options)
        break
      case ProcessFlowStepType.FindData:
        step = new ProcessFlowFindDataStep(parent, options)
        break
      case ProcessFlowStepType.ExportCSV:
        step = new ProcessFlowExportCSVStep(parent, options)
        break
      case ProcessFlowStepType.FindCount:
        step = new ProcessFlowFindCountStep(parent, options)
        break
      case ProcessFlowStepType.TakeAction:
        step = new ProcessFlowTakeActionStep(parent, options)
        break
      case ProcessFlowStepType.DisplayAForm:
        step = new ProcessFlowDisplayAFormStep(parent, options)
        break
      case ProcessFlowStepType.Redirect:
        if ('step_type' in parent) {
          throw new Error('Redirect steps cannot be inside iteration steps.')
        } else {
          step = new ProcessFlowRedirectStep(parent, options)
        }
        break
      default:
        assertNever(options.stepType);
        break
    }

    // steps with actions don't have the single +jump_to+ on step level but each action has one
    if (step.actionsEnabled) {
      if (!step.hasActions) {
        step.appendAction({title: 'Cancel', style: 'grey-outline', sort_order: 0, validate_fields: false }, false);
        step.appendAction({title: 'Submit', style: 'green', sort_order: 1 }, false);
      }
    } else if (!step.jump_to) {
      ProcessFlowStepJumpToFactory.findOrCreate(step, options.json.jump_to);
    }

    if (options.firstCreation && step instanceof IterationStep && !step.initialStep) {
      const initialStep = this.create(step, { stepType: ProcessFlowStepType.InitialStep })
      step.initializeEmbedded(step, initialStep)
    }

    return step
  }

  static findOrCreate(idsToCheck: string | string[], processFlow: ProcessFlow, stepType: ProcessFlowStepType, json: object = {}): ProcessFlowStep {
    let step: ProcessFlowStep = null
    for (const id of InstanceHelper.toArray(idsToCheck)) {
      step = ProcessFlowStep.repo.find(id)
      if (step) return step
    }
    return ProcessFlowStepFactory.create(processFlow, { stepType, json })
  }
}